import { axios } from "../../../core/axios";
import type { ContractProduct } from '@/components/organisms/ContractDetailsDrawer/ContractDetailsDrawer';

// ─── Types ───────────────────────────────────────────────────
// Shape of a single line item returned by the BE get-contract-info endpoint,
// which passes through WSGetContractInfo's ContractInfo array. Only the
// fields the mapper consumes are typed; upstream returns ~30+ additional
// agency/commission/fee metadata fields.

export interface ContractInfoItem {
    ContractCliId: string;
    ContractId: string;
    ContractDetailId: string;
    PckId: string;
    PckName: string;
    PrdId: string;
    PrdName: string;
    ProgramId: string;
    ContractDtlBudgetAmt: string;
    ContractDtlBudgetFromDate: string;
    ContractDtlBudgetToDate: string;
    NetBudget: string;
}

export interface GetContractInfoResponse {
    contractedProducts: ContractProduct[];
}

// ─── Mapper (pure) ───────────────────────────────────────────

// Deterministic palette — cycles by sorted-product-name index so the same
// product always gets the same color within a response.
const PRODUCT_COLOR_PALETTE = [
    '#539995',
    '#dc5c1f',
    '#4f9a9b',
    '#e8a020',
    '#7b61c4',
    '#9b7b9b',
    '#2a6f6b',
    '#4896FE',
];

const toNumber = (value: string): number => {
    const n = parseFloat(value);
    return Number.isFinite(n) ? n : 0;
};

/**
 * Groups raw ContractInfo line items by `PckName` and maps each group into a
 * ContractProduct with one period per line item. `isMediaProduct` is inferred
 * from the relationship between gross budget and net budget: when net is
 * strictly less than gross (net = gross minus agency commission), it's a
 * media buy and we surface `targetMediaSpend`. Otherwise net has ambiguous
 * semantics across products and is hidden.
 */
export const mapContractInfoToProducts = (items: ContractInfoItem[]): ContractProduct[] => {
    const grouped = new Map<string, ContractInfoItem[]>();
    for (const item of items) {
        // Prefer the product name; fall back to the package name when PrdName
        // is empty (common in the UAT data where products are identified only
        // by PrdId and the package label).
        const name =
            item.PrdName?.trim() ||
            item.PckName?.trim() ||
            `Product ${item.PrdId ?? ''}`.trim();
        const bucket = grouped.get(name) ?? [];
        bucket.push(item);
        grouped.set(name, bucket);
    }

    const sortedNames = Array.from(grouped.keys()).sort();

    return sortedNames.map((name, index) => {
        const bucket = grouped.get(name)!;
        const sorted = [...bucket].sort((a, b) =>
            a.ContractDtlBudgetFromDate.localeCompare(b.ContractDtlBudgetFromDate),
        );

        const periods = sorted.map((item) => {
            const grossBudget = toNumber(item.ContractDtlBudgetAmt);
            const netBudget = toNumber(item.NetBudget);
            const isMedia = netBudget > 0 && netBudget < grossBudget;

            return {
                period: `${item.ContractDtlBudgetFromDate} – ${item.ContractDtlBudgetToDate}`,
                grossBudget,
                targetMediaSpend: isMedia ? netBudget : undefined,
            };
        });

        // A product is considered a media product if any of its periods
        // exhibits the gross > net commission pattern.
        const isMediaProduct = periods.some((p) => p.targetMediaSpend != null);

        return {
            name,
            color: PRODUCT_COLOR_PALETTE[index % PRODUCT_COLOR_PALETTE.length],
            isMediaProduct,
            periods,
        };
    });
};

// ─── Endpoint ────────────────────────────────────────────────

export const contractEndpoints = {
    /**
     * Fetch contract info for a dealer and map it into the drawer's
     * `ContractProduct[]` shape. Calls the BE `/get-contract-info/{clientId}`
     * endpoint which proxies WSGetContractInfo.
     */
    getContractInfo: async (dealerRefId: string | number): Promise<GetContractInfoResponse> => {
        const date = new Date().toISOString().slice(0, 10);
        const { data } = await axios.get<{ contractInfo: ContractInfoItem[] }>(
            `/get-contract-info/${dealerRefId}`,
            { params: { date } },
        );

        return {
            contractedProducts: mapContractInfoToProducts(data.contractInfo ?? []),
        };
    },
};
